import { useNode } from "@craftjs/core";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import useApplyGlobalSettings, { CODEBLOCK_TYPES } from "../../../../hooks/useApplyGlobalSettings";
import { usePaletteColor } from "../../../../hooks/usePaletteColor";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import { dummyGridImageObject } from "../../../../utils/helpers";
import { colors } from "../../../../utils/theme";
import { BlockIndicator } from "../../Shared/BlockIndicator";
import ImageBlock from "../../Shared/ImageBlock";
import ImageGridSettings from "./ImageGridSettings";
import {
  applyQuiltedPattern,
  balanceMasonryColumns,
  buildContainSrc,
} from "./gridLayout";

const GRID_TYPES = {
  STANDARD: "standard",
  QUILTED: "quilted",
  MASONRY: "masonry",
  EVEN: "even",
};

// ---------------------------------------------------------------------------
// CSS-Grid renderer — preserve-ratio + flush. Pure styled-components; no MUI.
// ---------------------------------------------------------------------------
const V2Wrapper = styled.div`
  padding: ${(props) => props.padding || "0"};
  width: 100%;
  display: flex;
  justify-content: ${(props) => props.alignment || "flex-start"};
`;

// Even / Standard: an equal-column CSS grid. Even uses fixed aspect-ratio cells
// (default 1:1); Standard lets rows size to natural-ratio content but stays a
// clean grid. Both fill the last (partial) row left-aligned and finish flush.
const V2Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  gap: ${(props) => props.gap || "10px"};
  width: 100%;
  align-items: start;
  justify-items: stretch;
`;

// Even-grid cell: a fixed aspect-ratio box (no ragged bottom), image contained
// and centred on a subtle palette-tinted fill so off-ratio images letterbox
// rather than crop.
const V2EvenCell = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${(props) => props.ratio || "1 / 1"};
  background: ${(props) => props.fill};
  border-radius: ${(props) => props.radius || 0}px;
  overflow: hidden;
  /* Older browsers without aspect-ratio support: padding-top fallback box. */
  @supports not (aspect-ratio: 1 / 1) {
    height: 0;
    padding-top: 100%;
  }
`;

// Masonry: N flex columns, balanced by greedy shortest-column placement so the
// column bottoms come out near-even. Each image keeps its natural ratio.
const V2MasonryRow = styled.div`
  display: flex;
  gap: ${(props) => props.gap || "10px"};
  width: 100%;
  align-items: flex-start;
`;
const V2MasonryCol = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.gap || "10px"};
`;

// Quilted: CSS-grid with deterministic col/row spans (no randomness).
const V2QuiltedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(${(props) => props.columns}, 1fr);
  grid-auto-rows: ${(props) => props.rowHeight || "120px"};
  grid-auto-flow: dense;
  gap: ${(props) => props.gap || "10px"};
  width: 100%;
`;
const V2QuiltedCell = styled.div`
  grid-column: span ${(props) => Math.min(props.colSpan || 1, props.columns)};
  grid-row: span ${(props) => props.rowSpan || 1};
  min-width: 0;
`;

export const ImageGrid = ({
  images,
  showHyperlink,
  hyperlink,
  isApplyMarginOnAllSides,
  allSideMargin,
  margin,
  borderRadius,
  borderColor,
  borderWidth,
  dropShadowColor,
  alignment,
  gridType,
  showCaptions,
  columns,
}) => {
  const {
    connectors: { connect, drag },
    actions: { setProp },
  } = useNode();

  const selectedSegment = useSelector(getSelectedSegment);
  const selectedSegmentColor = selectedSegment?.settings?.color;
  const { imageGrid: imageGridProps } = useSelector((state) => state.codeblockStyles);
  const dispatch = useDispatch();

  const { getPaletteColor, getSegmentColor } = usePaletteColor();

  useEffect(() => {
    // Set the props from the reducer if they're undefined
    const timeoutId = setTimeout(() => {
      setProp((props) => {
        // Margin properties
        if (props.margin === undefined) {
          props.margin = { ...imageGridProps.margin };
        }
        
        if (props.isApplyMarginOnAllSides === undefined) {
          props.isApplyMarginOnAllSides = imageGridProps.isApplyMarginOnAllSides;
        }
        
        if (props.allSideMargin === undefined) {
          props.allSideMargin = imageGridProps.allSideMargin;
        }
        
        // Border properties
        if (props.borderRadius === undefined) {
          props.borderRadius = imageGridProps.borderRadius;
        }
        
        if (props.borderWidth === undefined) {
          props.borderWidth = imageGridProps.borderWidth;
        }
        
        if (props.borderColor === undefined) {
          props.borderColor = imageGridProps.borderColor || "suggested_color_0";
        }
        
        // Other style properties
        if (props.dropShadowColor === undefined) {
          props.dropShadowColor = imageGridProps.dropShadowColor;
        }
        
        if (props.alignment === undefined) {
          props.alignment = imageGridProps.alignment;
        }
        
        // ImageGrid-specific properties
        if (props.showCaptions === undefined) {
          props.showCaptions = imageGridProps.showCaptions;
        }
        
        if (props.gridType === undefined) {
          // A NEW block defaults to the Even Grid (Gordon 2026-06-14); users
          // still switch styles freely in the Layout panel.
          props.gridType = GRID_TYPES.EVEN;
        }
        
        if (props.columns === undefined) {
          props.columns = imageGridProps.columns;
        }
      });
    }, 5);
    
    // Cleanup timeout if the component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, []);

  // Use the hook to apply global settings when they change
  useApplyGlobalSettings(setProp, CODEBLOCK_TYPES.ImageGrid);

  const marginStyle = isApplyMarginOnAllSides
    ? `${allSideMargin}px`
    : margin
    ? `${margin?.top}px ${margin?.right}px ${margin?.bottom}px ${margin?.left}px`
    : undefined;

  const border = borderColor
    ? `${borderWidth}px solid ${getPaletteColor(borderColor)}`
    : "none";

  // -------------------------------------------------------------------------
  // CSS-Grid renderer. Preserve-ratio + flush/balanced. The shared per-image
  // renderer below contains each image (object-fit: contain) so off-ratio
  // images letterbox, never crop.
  // -------------------------------------------------------------------------
  const cols = Math.max(1, Math.min(Number(columns) || 1, 4));
  // G7: Quilted spans are 2-wide; at columns<2 every span clamps to 1 and the
  // pattern collapses to a plain single column. Force a minimum of 2 columns
  // for Quilted only (least-surprising: keeps Quilted recognisably quilted; all
  // other grid types still honour the user's column count exactly).
  const quiltedCols = Math.max(2, cols);
  // G5: match v1 masonry spacing (MUI ImageList gap={8}) so flipping the flag
  // doesn't shift masonry gutters; all other v2 grids keep the 10px default.
  const v2Gap = gridType === GRID_TYPES.MASONRY ? "8px" : "10px";

  // G2: derive the per-cell width from the live container width / column count
  // instead of a hardcoded 320, so the CDN request matches the rendered size.
  // ResizeObserver on the wrapper; defaults to 320 before first measure.
  const v2WrapperRef = useRef(null);
  const [measuredCellWidth, setMeasuredCellWidth] = useState(320);
  useEffect(() => {
    const el = v2WrapperRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const colCount = gridType === GRID_TYPES.QUILTED ? quiltedCols : cols;
    const gapPx = gridType === GRID_TYPES.MASONRY ? 8 : 10;
    const compute = (containerWidth) => {
      if (!isFinite(containerWidth) || containerWidth <= 0) return;
      const totalGap = gapPx * Math.max(0, colCount - 1);
      const per = Math.round((containerWidth - totalGap) / colCount);
      if (isFinite(per) && per > 0) setMeasuredCellWidth(per);
    };
    compute(el.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) compute(entry.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [cols, quiltedCols, gridType]);

  const cellWidthPx = measuredCellWidth;

  // T22.138: caption bar = border colour (even at width 0), else segment colour.
  const captionColors = {
    captionBorderColor: borderColor ? getPaletteColor(borderColor) : undefined,
    captionSegmentColor: getSegmentColor(),
  };

  const sharedImageStyle = {
    boxShadow:
      dropShadowColor &&
      `0px 0px 9.3px 0px ${getPaletteColor(dropShadowColor)}`,
    borderRadius,
    border,
  };

  // Render one contained image. cellWidthPx drives the width-constrained CDN
  // URL; the image fills its cell at natural ratio (contain) — no crop.
  // `natural` (Standard/Masonry): height follows the image's own aspect ratio.
  // Without it (Even/Quilted) the image fills its fixed-ratio cell — needed
  // because ImageBlock defaults to height:100%, which inside a CSS-grid row
  // stretches every item to the tallest sibling (the ISP-4009 "still square /
  // equal heights" report).
  const renderV2Image = (item, cellWidthPx, fillBg, natural = false) => {
    const { src, srcSet } = buildContainSrc(item.src, cellWidthPx);
    const naturalStyle = natural ? { height: "auto" } : null;
    return (
      <ImageBlock
        key={item.id || item.src}
        src={src}
        srcSet={srcSet}
        title={item.title}
        showImageTitle={showCaptions}
        {...captionColors}
        showHyperlink={showHyperlink}
        hyperlink={hyperlink}
        style={{ ...sharedImageStyle, background: fillBg, ...naturalStyle }}
        imageStyle={{ objectFit: "contain", ...naturalStyle }}
        alt={item.title}
        isImageLoading={item?.loading}
        loading="lazy"
      />
    );
  };

  const renderV2 = (imgs = []) => {
    // EVEN GRID — equal aspect-ratio cells, contained + letterboxed, flush.
    if (gridType === GRID_TYPES.EVEN) {
      return (
        <V2Grid columns={cols} gap={v2Gap}>
          {imgs.map((item) => (
            <V2EvenCell
              key={item.id || item.src}
              ratio="1 / 1"
              radius={borderRadius || 0}
              fill={colors.NeutralBackground}
            >
              {renderV2Image(item, cellWidthPx, colors.NeutralBackground)}
            </V2EvenCell>
          ))}
        </V2Grid>
      );
    }

    // MASONRY — balanced columns (greedy shortest-column), natural ratio.
    if (gridType === GRID_TYPES.MASONRY) {
      const masonryCols = balanceMasonryColumns(imgs, cols);
      return (
        <V2MasonryRow gap={v2Gap}>
          {masonryCols.map((colImages, ci) => (
            <V2MasonryCol key={ci} gap={v2Gap}>
              {colImages.map((item) =>
                renderV2Image(item, cellWidthPx, undefined, true)
              )}
            </V2MasonryCol>
          ))}
        </V2MasonryRow>
      );
    }

    // QUILTED — deterministic span pattern via CSS-grid (no randomness).
    if (gridType === GRID_TYPES.QUILTED) {
      const quilted = applyQuiltedPattern(imgs);
      return (
        <V2QuiltedGrid columns={quiltedCols} gap={v2Gap} rowHeight="140px">
          {quilted.map((item) => (
            <V2QuiltedCell
              key={item.id || item.src}
              columns={quiltedCols}
              colSpan={item.cols}
              rowSpan={item.rows}
            >
              {/* multi-column tiles are wider, so request a proportionally
                  larger CDN image to avoid blur on 2-col spans. */}
              {renderV2Image(
                item,
                cellWidthPx * (item.cols || 1),
                colors.NeutralBackground
              )}
            </V2QuiltedCell>
          ))}
        </V2QuiltedGrid>
      );
    }

    // STANDARD (default) — equal-width columns, each image at its NATURAL
    // aspect ratio (variable height, no forced 1:1 cell); rows top-align via
    // the grid's align-items: start. Only the Even Grid uses fixed-ratio
    // cells (ISP-4009: Standard must not render as square).
    return (
      <V2Grid columns={cols} gap={v2Gap}>
        {imgs.map((item) => renderV2Image(item, cellWidthPx, undefined, true))}
      </V2Grid>
    );
  };

  return (
    <div ref={(ref) => connect(drag(ref))}>
      <BlockIndicator>
        <V2Wrapper ref={v2WrapperRef} padding={marginStyle} alignment={alignment}>
          {images?.length ? (
            renderV2(images)
          ) : (
            // G12: wrap the empty-state dummy in the same cell as real images
            // so the layout doesn't jump when the first image is added.
            <V2Grid columns={cols} gap={v2Gap}>
              <V2EvenCell
                ratio="1 / 1"
                radius={borderRadius || 0}
                fill={colors.NeutralBackground}
              >
                <ImageBlock
                  key={dummyGridImageObject.src}
                  src={dummyGridImageObject.src}
                  title={dummyGridImageObject.title}
                  showImageTitle={showCaptions}
                  {...captionColors}
                  style={{ borderRadius, border, background: colors.NeutralBackground }}
                  imageStyle={{ objectFit: "contain" }}
                  alt="Add images to the grid"
                  loading="lazy"
                />
              </V2EvenCell>
            </V2Grid>
          )}
        </V2Wrapper>
      </BlockIndicator>
    </div>
  );
};

export const ImageGridDefaultProps = {
  images: [],
  shallowImages: [],
  margin: undefined,
  isApplyMarginOnAllSides: undefined,
  allSideMargin: undefined,
  borderRadius: undefined,
  borderWidth: undefined,
  showCaptions: undefined,
  dropShadowColor: undefined,
  alignment: undefined,
  gap: "10px",
  gridType: undefined,
  borderColor: undefined,
  columns: undefined,
};

ImageGrid.craft = {
  props: ImageGridDefaultProps,
  related: {
    settings: ImageGridSettings,
  },
};
